import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useMemo } from 'react'
import { apiClient } from '@/lib/api/client'
import type { POFilters } from '@/store/uiStore'

function normalizeFilters(f: POFilters): Record<string, string> {
  return Object.fromEntries(
    Object.entries(f).filter(([, v]) => v !== undefined && v !== '')
  ) as Record<string, string>
}

export function usePOs(filters: POFilters = {}) {
  const normalized = normalizeFilters(filters)
  return useQuery({
    queryKey: ['pos', normalized] as const,
    queryFn: async () => {
      const { data, error } = await apiClient.GET('/api/purchase-orders', {
        params: { query: normalized },
      })
      if (error) throw new Error('Failed to load purchase orders')
      return data
    },
  })
}

export function useAllPOs() {
  return usePOs({})
}

export function usePOCounts() {
  const { data: all = [] } = useAllPOs()
  return useMemo(
    () => ({
      ALL: all.length,
      DRAFT: all.filter((p) => p.status === 'DRAFT').length,
      AWAITING_APPROVAL: all.filter((p) => p.status === 'AWAITING_APPROVAL').length,
      APPROVED: all.filter((p) => p.status === 'APPROVED').length,
      ORDERED: all.filter(
        (p) => p.status === 'ORDERED' || p.status === 'PARTIAL_ORDERED'
      ).length,
      ARCHIVED: all.filter((p) => p.status === 'ARCHIVED').length,
    }),
    [all]
  )
}

export function usePO(poId: string | null) {
  return useQuery({
    queryKey: ['po', poId] as const,
    queryFn: async () => {
      const { data, error } = await apiClient.GET('/api/purchase-orders/{po_id}', {
        params: { path: { po_id: poId! } },
      })
      if (error) throw new Error(`Failed to load PO ${poId}`)
      return data
    },
    enabled: poId !== null,
  })
}

export function useLocations() {
  return useQuery({
    queryKey: ['locations'] as const,
    queryFn: async () => {
      const { data } = await apiClient.GET('/api/settings/locations', {})
      return data?.locations ?? []
    },
    staleTime: Infinity,
  })
}

export function usePOBudget() {
  return useQuery({
    queryKey: ['po-budget'] as const,
    queryFn: async () => {
      const { data, error } = await apiClient.GET('/api/purchase-orders/budget', {})
      if (error) throw new Error('Failed to load budget')
      return data
    },
    staleTime: 60_000,
  })
}

// Archive each selected PO sequentially. Backend's /archive only accepts
// ORDERED or PARTIAL_ORDERED — caller is responsible for filtering selection.
export function useArchivePOs() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: async (poIds: string[]) => {
      const failures: { po_id: string; error: string }[] = []
      for (const poId of poIds) {
        const { error } = await apiClient.POST(
          '/api/purchase-orders/{po_id}/archive',
          { params: { path: { po_id: poId } } }
        )
        if (error) {
          failures.push({
            po_id: poId,
            error:
              (error as { detail?: string })?.detail ?? 'archive failed',
          })
        }
      }
      return { archived: poIds.length - failures.length, failures }
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['pos'] })
      qc.invalidateQueries({ queryKey: ['po-budget'] })
    },
  })
}

export function useMergePOs() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: async (poIds: string[]) => {
      const { data, error } = await apiClient.POST(
        '/api/purchase-orders/merge',
        { body: { po_ids: poIds } }
      )
      if (error) {
        const detail =
          (error as { detail?: string })?.detail ?? 'merge failed'
        throw new Error(detail)
      }
      return data
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['pos'] })
    },
  })
}
